import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import logoOuraTrust from "@/assets/logo-ouratrust-v2.png";
import { useLanguage } from "@/hooks/useLanguage";
import { localizePublicPath } from "@/lib/publicRoutes";
import { trackEvent } from "@/lib/ga4";

export const Footer = () => {
  const { t, language } = useLanguage();

  const content = {
    fr: {
      tagline: "Le cockpit premium pour piloter réputation, présence Google et visibilité locale.",
      compliance: "Connexions officielles et usage conforme des plateformes.",
      apiNotice: "Google & Facebook via intégrations officielles quand elles sont disponibles. Trustpilot & TripAdvisor suivis dans le même espace.",
      complianceDisclaimer:
        "OuraTrust ne modifie pas, ne supprime pas et ne bloque pas les avis publiés. La plateforme centralise les signaux, prépare les réponses et structure un pilotage local plus clair.",
      googleDataUsage: "Utilisation des données Google API",
    },
    en: {
      tagline: "The premium cockpit for managing reputation, Google presence and local visibility.",
      compliance: "Official integrations and compliant platform usage.",
      apiNotice: "Google & Facebook through official integrations where available. Trustpilot & TripAdvisor monitored in the same workspace.",
      complianceDisclaimer:
        "OuraTrust does not modify, suppress or block published reviews. It centralizes signals, prepares replies and gives teams a clearer local operating view.",
      googleDataUsage: "Google API Data Usage",
    },
  };

  const page = content[language];

  return (
    <footer className="border-t border-border bg-gradient-to-b from-muted/20 to-background" aria-label="Site footer">
      <div className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.35fr_0.8fr_0.8fr]">
          <div className="max-w-lg">
            <Link to={localizePublicPath("/", language)} className="inline-flex items-center">
              <img src={logoOuraTrust} alt="OuraTrust" className="h-7 object-contain" width={185} height={28} />
            </Link>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">{page.tagline}</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold text-foreground shadow-sm">
              <Shield size={14} className="text-primary" />
              {page.compliance}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t.resources}</h4>
            <ul className="mt-4 space-y-3">
              <li><Link to={localizePublicPath("/how-it-works", language)} onClick={() => trackEvent("click_footer_how_it_works", { language })} className="text-sm text-foreground transition-colors hover:text-primary">{t.footerHowItWorks}</Link></li>
              <li><Link to={localizePublicPath("/pricing", language)} onClick={() => trackEvent("click_footer_pricing", { language })} className="text-sm text-foreground transition-colors hover:text-primary">{t.footerPricing}</Link></li>
              <li><Link to={localizePublicPath("/about", language)} className="text-sm text-foreground transition-colors hover:text-primary">{t.footerAbout}</Link></li>
              <li><Link to={localizePublicPath("/blog", language)} className="text-sm text-foreground transition-colors hover:text-primary">{t.blog}</Link></li>
              <li><Link to={localizePublicPath("/contact", language)} onClick={() => trackEvent("click_contact", { language, cta_location: "footer" })} className="text-sm text-foreground transition-colors hover:text-primary">{t.contact}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t.footerLegal}</h4>
            <ul className="mt-4 space-y-3">
              <li><Link to={localizePublicPath("/privacy-policy", language)} onClick={() => trackEvent("click_footer_privacy", { language })} className="text-sm text-foreground transition-colors hover:text-primary">{t.privacyPolicy}</Link></li>
              <li><Link to={localizePublicPath("/terms", language)} className="text-sm text-foreground transition-colors hover:text-primary">{t.termsOfService}</Link></li>
              <li><Link to={localizePublicPath("/legal-notice", language)} className="text-sm text-foreground transition-colors hover:text-primary">{t.legalMentions}</Link></li>
              <li><Link to={localizePublicPath("/data-deletion", language)} onClick={() => trackEvent("click_footer_data_deletion", { language })} className="text-sm text-foreground transition-colors hover:text-primary">{t.dataDeletion}</Link></li>
              <li><Link to={localizePublicPath("/google-data-usage", language)} onClick={() => trackEvent("click_footer_google_data_usage", { language })} className="text-sm text-foreground transition-colors hover:text-primary">{page.googleDataUsage}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-card/70 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{page.compliance}</p>
          <p className="mt-2 text-sm leading-7 text-muted-foreground">{page.apiNotice}</p>
          <p className="mt-1 max-w-4xl text-[11px] leading-6 text-muted-foreground">{page.complianceDisclaimer}</p>
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-border pt-6 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
          <span className="text-xs">{t.footerCopyright}</span>
          <span className="text-xs">support@ouratrust.com</span>
        </div>
      </div>
    </footer>
  );
};
